import React from 'react';
import { Progress, type UploadFile, Button as AntdButton } from 'antd';
import { DownloadIcon, Trash2Icon } from 'lucide-react';
import type { ApiImportData } from '@dc-trans/data-layer';
import excelIcon from '@/assets/excel.svg';

interface FileListItemProps {
  file: UploadFile;
  uploadResult: ApiImportData | null;
  uploadError: string;
  isLoading: boolean;
  onRemove: (_file: UploadFile) => void;
  onDownloadErrorFeedback: () => void;
}

export const FileListItem: React.FC<FileListItemProps> = ({
  file,
  uploadResult,
  uploadError,
  isLoading,
  onRemove,
  onDownloadErrorFeedback,
}) => {
  const hasErrors = (uploadResult?.total_error || 0) > 0 && !isLoading;
  const hasPartialSuccess = (uploadResult?.total_inserted || 0) > 0;

  return (
    <div
      className={`w-full border-dashed ${
        file.status === 'error' || hasErrors || uploadError
          ? 'border-red-500'
          : file.status === 'done'
            ? 'border-green-500'
            : 'border-gray-400'
      } border-[1px] rounded-xl py-2 px-3 flex flex-row items-center gap-1 h-16`}
    >
      <img src={excelIcon} className="w-8 h-8" alt="Excel file" />

      <div className="flex flex-col flex-1 overflow-hidden">
        <p className="text-sm font-bold truncate">{file.name}</p>

        {/* Error Messages */}
        {hasErrors && (
          <p className="text-red-500 text-sm truncate">
            {hasPartialSuccess ? `Partial Upload, ${uploadResult?.total_error} ` : 'Gagal Upload, '}
            data tidak sesuai
          </p>
        )}

        {uploadError && !isLoading && <p className="text-red-500 text-sm truncate">{uploadError}</p>}
        {file.status === 'done' && !uploadError && !hasErrors && !isLoading && (
          <p className="text-green-500 text-sm truncate">Completed</p>
        )}

        {file.status === 'done' && isLoading && <p className="text-sm truncate italic">Validating...</p>}

        {/* Progress Bar */}
        {file.status !== 'done' && !hasErrors && !uploadError && (
          <Progress
            percent={file.percent ?? 0}
            showInfo={file?.percent ? file.percent > 0 : false}
            strokeColor={'oklch(60.9% 0.126 221.723)'}
            trailColor="#D5FAFD"
            status={'active'}
          />
        )}
      </div>

      {/* Action Buttons */}
      {hasErrors && (
        <AntdButton
          type="link"
          shape="circle"
          onClick={onDownloadErrorFeedback}
          icon={<DownloadIcon color="gray" size={24} />}
        />
      )}

      {!isLoading && file.status !== 'done' && (
        <AntdButton
          variant="link"
          shape="circle"
          color="red"
          onClick={() => onRemove(file)}
          icon={<Trash2Icon size={24} />}
        />
      )}
    </div>
  );
};

export default FileListItem;
